import BigNumber from 'bignumber.js'
import { getPrecison } from './utils'
import i18n from '@/services/i18n'

export interface RuleItem {
  validator: RegExp | ((value: string) => boolean)
  message: string
}

export const RuleRegExp = {
  required: /^\w+$/,
  requiredWithBlank: /.+/,
  int: /^-?[0-9]+$/,
  double: /(^[1-9]\d*\.\d+$|^0\.\d+$|^[1-9]\d*$|^0$)/,
  verificationCode: /^\d{6}$/,
}

export const validate = (value: string, rules: RuleItem[]) => {
  let isValid = true
  let errMsg = ''
  for (let index in rules) {
    const validator = rules[index].validator
    if (typeof validator === 'function') {
      isValid = validator(value)
    } else {
      isValid = validator.test(value)
    }
    if (!isValid) {
      errMsg = rules[index].message
      break
    }
  }
  return {
    isValid,
    errMsg,
  }
}

export const genMinOrEqualRule = (mini: string | number, message: string): RuleItem => {
  return {
    validator: (text: string) => new BigNumber(text).isGreaterThanOrEqualTo(new BigNumber(mini)),
    message: message,
  }
}

export const genMinRule = (mini: string | number, message: string): RuleItem => {
  return {
    validator: (text: string) => new BigNumber(text).isGreaterThan(new BigNumber(mini)),
    message: message,
  }
}

export const genMaxOrEqualRule = (max: string | number, message: string): RuleItem => {
  return {
    validator: (text: string) => new BigNumber(text).isLessThanOrEqualTo(new BigNumber(max)),
    message: message,
  }
}

export const genMaxRule = (max: string | number, message: string): RuleItem => {
  return {
    validator: (text: string) => new BigNumber(text).isLessThan(new BigNumber(max)),
    message: message,
  }
}

export const genInt = (): RuleItem => ({
  validator: (text: string) => {
    return RuleRegExp.int.test(text)
  },
  message: i18n.t('common.validate.int'),
})

export const genIntOrDouble = (): RuleItem => ({
  validator: (text: string) => {
    return RuleRegExp.int.test(text) || RuleRegExp.double.test(text)
  },
  message: i18n.t('common.validate.number'),
})

export const precision = (p: number): RuleItem => {
  return {
    validator(value: string) {
      const valuePrecision = getPrecison(value)
      return valuePrecision <= p
    },
    message: i18n.t('common.validate.decimal', { decimal: p }),
  }
}

export const genVerificationCode = (): RuleItem => ({
  validator: (text: string) => {
    return RuleRegExp.verificationCode.test(text)
  },
  message: i18n.t('common.validate.verificationCode'),
})
